import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PermissionProvider } from './contexts/PermissionContext';
import { FeatureProvider } from './contexts/FeatureContext';
import { AlertProvider } from './contexts/AlertContext';
import { DrillDownProvider } from './contexts/DrillDownContext';
import { Login } from './pages/Login';
import { Register } from './pages/Register';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetPassword } from './pages/ResetPassword';
import { PrivacyPolicy } from './pages/PrivacyPolicy';
import { TermsOfService } from './pages/TermsOfService';
import { ContactSupport } from './pages/ContactSupport';
import { CarrierDashboard } from './pages/CarrierDashboard';
import { BrokerDashboard } from "./pages/BrokerDashboard";
import CarrierComparePage from "./pages/CarrierComparePage";
import BulkScreeningPage from "./pages/BulkScreeningPage";
import { AdminDashboard } from './pages/AdminDashboard';
import { SystemMetricsPage } from './pages/SystemMetricsPage';
import { LandingPage } from './pages/LandingPage';
import VINLookup from './pages/VINLookup';
import StatsDashboard from './pages/StatsDashboard';
import AlertsPage from './pages/AlertsPage';
import AlertPreferencesPage from './pages/AlertPreferencesPage';
import WatchlistPage from './pages/WatchlistPage';
import MonitoredCarriersPage from './pages/MonitoredCarriersPage';
import VinMatchesPage from './pages/VinMatchesPage';
import MergeCarriersPage from './pages/MergeCarriersPage';
import MarketIntelligence from './pages/MarketIntelligence';
import GeographicIntelligence from './pages/GeographicIntelligence';
import InsuranceIntelligence from './pages/InsuranceIntelligence';
import ShipperAuditPage from './pages/ShipperAuditPage';
// v1 ShipperProfilePage removed 2026-05-21; /shipper-audit/:name now redirects to v2
const ShipperProfileV1Redirect: React.FC = () => {
  const { shipperName } = useParams<{ shipperName: string }>();
  return <Navigate to={`/shipper-audit-v2/${encodeURIComponent(shipperName ?? '')}`} replace />;
};
const ShipperProfileV2Page = lazy(() => import('./pages/ShipperProfileV2'));
const CarriersByLocation = lazy(() => import('./pages/CarriersByLocation'));
import DnuAnalyticsPage from './pages/DnuAnalyticsPage';
import TagsManagementPage from './pages/TagsManagementPage';
import { LoadingPage } from './components/shared/LoadingSpinner';
import { ErrorBoundary } from './components/ErrorBoundary';
import DrillDownModal from './components/DrillDownModal';
import SMTPSettings from './pages/admin/SMTPSettings';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import UserManagement from './pages/UserManagement';
import CompanyManagement from './pages/CompanyManagement';
import AuditLogViewer from './pages/AuditLogViewer';
import { RoleManagement } from './pages/admin/RoleManagement';
import { RegistrationRequests } from './pages/admin/RegistrationRequests';
import Unauthorized from './pages/Unauthorized';
import NotFound from './pages/NotFound';
import ComingSoon from './pages/ComingSoon';
import {
  ProtectedRoute as RBACProtectedRoute,
  SuperAdminRoute,
} from './components/rbac';
import './styles/drill-down.css';

// Basic Protected Route Component (for non-RBAC routes)
const BasicProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingPage />;
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
};

// Keep ProtectedRoute as alias for backward compatibility with existing routes
const ProtectedRoute = BasicProtectedRoute;

// Dashboard Router - routes based on user type
const DashboardRouter: React.FC = () => {
  const { user } = useAuth();

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  switch (user.userType) {
    case 'carrier':
      return <CarrierDashboard />;
    case 'broker':
      return <BrokerDashboard />;
    case 'admin':
    case 'government':
      return <AdminDashboard />;
    default:
      return <CarrierDashboard />;
  }
};

// Carrier Search - Always goes to CarrierDashboard regardless of user type
const CarrierSearchPage: React.FC = () => {
  return <CarrierDashboard />;
};

// Main App Routes wrapped with AlertProvider and DrillDownProvider
const AppRoutes: React.FC = () => {
  return (
    <AlertProvider>
      <DrillDownProvider syncWithUrl={true}>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="/unauthorized" element={<Unauthorized />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/contact" element={<ContactSupport />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardRouter />
            </ProtectedRoute>
          }
        />
        <Route
          path="/carrier-search"
          element={
            <ProtectedRoute>
              <CarrierSearchPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/carriers-by-location"
          element={
            <ProtectedRoute>
              <Suspense fallback={<LoadingPage />}>
                <CarriersByLocation />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/carrier-compare"
          element={
            <ProtectedRoute>
              <CarrierComparePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/bulk-screening"
          element={
            <ProtectedRoute>
              <BulkScreeningPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <RBACProtectedRoute
              permissions={['admin.users.view', 'admin.companies.view', 'admin.roles.view']}
            >
              <AdminDashboard />
            </RBACProtectedRoute>
          }
        />
        <Route
          path="/vin-lookup"
          element={
            <RBACProtectedRoute permission="vins.lookup">
              <VINLookup />
            </RBACProtectedRoute>
          }
        />
        <Route
          path="/statistics"
          element={
            <RBACProtectedRoute permission="analytics.view">
              <StatsDashboard />
            </RBACProtectedRoute>
          }
        />
        <Route
          path="/market-intelligence"
          element={
            <RBACProtectedRoute permission="analytics.market_intelligence">
              <MarketIntelligence />
            </RBACProtectedRoute>
          }
        />
        <Route
          path="/geographic"
          element={
            <RBACProtectedRoute permission="analytics.market_intelligence">
              <GeographicIntelligence />
            </RBACProtectedRoute>
          }
        />
        <Route
          path="/insurance-intelligence"
          element={
            <RBACProtectedRoute permission="analytics.market_intelligence">
              <InsuranceIntelligence />
            </RBACProtectedRoute>
          }
        />
        <Route
          path="/shipper-audit"
          element={
            <RBACProtectedRoute permission="shippers.audit">
              <ShipperAuditPage />
            </RBACProtectedRoute>
          }
        />
        <Route
          path="/shipper-audit/:shipperName"
          element={<ShipperProfileV1Redirect />}
        />
        <Route
          path="/shipper-audit-v2/:shipperName"
          element={
            <RBACProtectedRoute permission="shippers.view">
              <Suspense fallback={<LoadingPage />}>
                <ShipperProfileV2Page />
              </Suspense>
            </RBACProtectedRoute>
          }
        />
        <Route
          path="/dnu-analytics"
          element={
            <RBACProtectedRoute permission="analytics.view">
              <DnuAnalyticsPage />
            </RBACProtectedRoute>
          }
        />
        <Route
          path="/tags-management"
          element={
            <RBACProtectedRoute permission="inspections.view">
              <TagsManagementPage />
            </RBACProtectedRoute>
          }
        />
        <Route
          path="/alerts"
          element={
            <RBACProtectedRoute permission="monitoring.alerts.view">
              <AlertsPage />
            </RBACProtectedRoute>
          }
        />
        <Route
          path="/alert-preferences"
          element={
            <RBACProtectedRoute permission="monitoring.alerts.manage">
              <AlertPreferencesPage />
            </RBACProtectedRoute>
          }
        />
        <Route
          path="/watchlist"
          element={
            <RBACProtectedRoute permission="monitoring.watchlist.view">
              <WatchlistPage />
            </RBACProtectedRoute>
          }
        />
        <Route
          path="/monitored"
          element={
            <RBACProtectedRoute permission="monitoring.watchlist.view">
              <MonitoredCarriersPage />
            </RBACProtectedRoute>
          }
        />
        <Route
          path="/vin-matches"
          element={
            <ProtectedRoute>
              <VinMatchesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/merge"
          element={
            <ProtectedRoute>
              <MergeCarriersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/smtp-settings"
          element={
            <RBACProtectedRoute permission="admin.system.settings">
              <SMTPSettings />
            </RBACProtectedRoute>
          }
        />
        <Route
          path="/admin/registration-requests"
          element={
            <RBACProtectedRoute permission="admin.users.view">
              <RegistrationRequests />
            </RBACProtectedRoute>
          }
        />
        <Route
          path="/admin/system-metrics"
          element={
            <RBACProtectedRoute permission="admin.system.stats">
              <SystemMetricsPage />
            </RBACProtectedRoute>
          }
        />
        <Route
          path="/super-admin"
          element={
            <SuperAdminRoute>
              <SuperAdminDashboard />
            </SuperAdminRoute>
          }
        />
        <Route
          path="/super-admin/users"
          element={
            <RBACProtectedRoute permission="admin.users.view">
              <UserManagement />
            </RBACProtectedRoute>
          }
        />
        <Route
          path="/super-admin/companies"
          element={
            <RBACProtectedRoute permission="admin.companies.view">
              <CompanyManagement />
            </RBACProtectedRoute>
          }
        />
        <Route
          path="/super-admin/audit-log"
          element={
            <RBACProtectedRoute permission="admin.system.audit">
              <AuditLogViewer />
            </RBACProtectedRoute>
          }
        />
        <Route
          path="/super-admin/roles"
          element={
            <RBACProtectedRoute permission="admin.roles.view">
              <RoleManagement />
            </RBACProtectedRoute>
          }
        />
        <Route
          path="/carrier/:dotNumber"
          element={
            <ProtectedRoute>
              <CarrierDashboard />
            </ProtectedRoute>
          }
        />
        {/* Placeholder routes for features under development */}
        <Route path="/broker" element={<ProtectedRoute><ComingSoon title="Broker Dashboard" description="The broker-specific dashboard is coming soon." /></ProtectedRoute>} />
        <Route path="/super-admin/settings" element={<SuperAdminRoute><ComingSoon title="System Settings" description="System configuration settings are coming soon." /></SuperAdminRoute>} />
        <Route path="/super-admin/database" element={<SuperAdminRoute><ComingSoon title="Database Management" description="Database management tools are coming soon." /></SuperAdminRoute>} />
        {/* Route Aliases - Redirect common expected paths to actual routes */}
        <Route path="/vin-intelligence" element={<Navigate to="/vin-lookup" replace />} />
        <Route path="/analytics" element={<Navigate to="/statistics" replace />} />
        <Route path="/stats" element={<Navigate to="/statistics" replace />} />
        <Route path="/shipper-intelligence" element={<Navigate to="/shipper-audit" replace />} />
        <Route path="/monitored-carriers" element={<Navigate to="/monitored" replace />} />
        <Route path="/merge-carriers" element={<Navigate to="/merge" replace />} />
        <Route path="/super-admin/logs" element={<Navigate to="/super-admin/audit-log" replace />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      <DrillDownModal />
      </DrillDownProvider>
    </AlertProvider>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <PermissionProvider>
          <FeatureProvider>
            <Router>
              <AppRoutes />
            </Router>
          </FeatureProvider>
        </PermissionProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
